import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import { Button, DataTable, StatusBadge, RowActions, Skeleton, RequiredMark, type DataTableColumn } from '@/components/ui';
import { Plus, Trash2 } from 'lucide-react';
import { ProductSelector } from '@/features/catalog/components/ProductSelector';
import {
  useEventTargetsQuery,
  useEventTargetMutation,
  type EventProductTarget,
  type EntryMode,
  type EventTargetRequest,
} from '../hooks/useEventTargetsQuery';

const ENTRY_MODE_OPTIONS: EntryMode[] = ['ORDER', 'QUANTITY', 'USER'];

const EMPTY_FORM: EventTargetRequest = {
  productId: '',
  entryMode: 'ORDER',
  minOrderAmount: null,
  minQuantity: null,
};

interface Props {
  eventId: string;
}

export const EventTargetForm = ({ eventId }: Props) => {
  const { t } = useTranslation('common');
  const query = useEventTargetsQuery(eventId);
  const { add, remove } = useEventTargetMutation(eventId);

  const [form, setForm] = useState<EventTargetRequest>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const items = query.data ?? [];

  const set = <K extends keyof EventTargetRequest>(key: K, val: EventTargetRequest[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const validEventId = !!eventId && eventId !== 'new';

  const handleAdd = async () => {
    // C-5: 이벤트가 아직 저장되지 않았으면(빈 eventId) 하위 리소스 호출 금지.
    if (!validEventId) {
      setError('먼저 이벤트를 저장한 뒤 대상 상품을 추가할 수 있습니다.');
      return;
    }
    if (!form.productId.trim()) {
      setError(t('cms.events.targets.productIdRequired'));
      return;
    }
    setError(null);
    try {
      await add.mutateAsync(form);
      setForm(EMPTY_FORM);
    } catch (e) {
      setError(friendlyMessage(e, t('cms.events.targets.addFailed')));
    }
  };

  const handleRemove = async (targetId: string) => {
    try {
      await remove.mutateAsync(targetId);
    } catch {
      // silently ignore for list items
    }
  };

  const columns: DataTableColumn<EventProductTarget>[] = [
    {
      key: 'productName',
      header: t('cms.events.targets.cols.product'),
      cell: (row) => (
        <span
          className="block max-w-[280px] truncate text-sm font-medium"
          style={{ color: 'var(--content-strong)' }}
          title={row.productName ?? undefined}
        >
          {row.productName ?? '(상품 정보 없음)'}
        </span>
      ),
    },
    {
      key: 'entryMode',
      header: t('cms.events.targets.cols.entryMode'),
      align: 'center',
      cell: (row) => (
        <StatusBadge variant="info" label={row.entryMode ? t(`cms.events.entryMode.${row.entryMode}`, { defaultValue: '-' }) : '-'} />
      ),
    },
    {
      key: 'minOrderAmount',
      header: t('cms.events.targets.cols.minOrderAmount'),
      align: 'right',
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.minOrderAmount != null ? row.minOrderAmount.toLocaleString() + '원' : '-'}
        </span>
      ),
    },
    {
      key: 'minQuantity',
      header: t('cms.events.targets.cols.minQuantity'),
      align: 'center',
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.minQuantity ?? '-'}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: t('cms.events.targets.cols.isActive'),
      align: 'center',
      cell: (row) => (
        <StatusBadge
          variant={row.isActive ? 'success' : 'info'}
          label={row.isActive ? t('cms.active') : t('cms.inactive')}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <RowActions>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleRemove(row.targetId)}
            disabled={remove.isPending}
            style={{ color: 'var(--status-danger-fg)', borderColor: 'var(--status-danger-fg)' }}
          >
            <Trash2 size={12} />
            {t('cms.delete')}
          </Button>
        </RowActions>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div
        className="rounded-lg p-4"
        style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
      >
        <h3 className="mb-3 text-sm font-semibold" style={{ color: 'var(--content-strong)' }}>
          {t('cms.events.targets.addTitle')}
        </h3>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-1 lg:col-span-1">
            <span className="text-xs font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('cms.events.targets.productId')}
              <RequiredMark />
            </span>
            <ProductSelector
              value={form.productId || null}
              onChange={(id) => set('productId', id ?? '')}
              placeholder={t('cms.events.targets.productSearchPlaceholder', '상품명으로 검색')}
              excludeIds={items.map((i) => i.productId)}
            />
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('cms.events.targets.entryMode')}
            </span>
            <select
              value={form.entryMode}
              onChange={(e) => set('entryMode', e.target.value as EntryMode)}
              className="rounded border px-3 py-1.5 text-sm"
              style={{ background: 'var(--surface-raised)', borderColor: 'var(--divider-strong)', color: 'var(--content-strong)' }}
            >
              {ENTRY_MODE_OPTIONS.map((m) => (
                <option key={m} value={m}>{t(`cms.events.entryMode.${m}`)}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('cms.events.targets.minOrderAmount')}
            </span>
            <input
              type="number"
              value={form.minOrderAmount ?? ''}
              onChange={(e) => set('minOrderAmount', e.target.value ? Number(e.target.value) : null)}
              placeholder="50000"
              min={0}
              className="rounded border px-3 py-1.5 text-sm"
              style={{ background: 'var(--surface-raised)', borderColor: 'var(--divider-strong)', color: 'var(--content-strong)' }}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('cms.events.targets.minQuantity')}
            </span>
            <input
              type="number"
              value={form.minQuantity ?? ''}
              onChange={(e) => set('minQuantity', e.target.value ? Number(e.target.value) : null)}
              placeholder="1"
              min={1}
              className="rounded border px-3 py-1.5 text-sm"
              style={{ background: 'var(--surface-raised)', borderColor: 'var(--divider-strong)', color: 'var(--content-strong)' }}
            />
          </label>
        </div>

        {/* [event-form-fix] 등록(미저장) 모드 — 종전엔 추가 버튼만 말없이 비활성이라
            (disabled 버튼은 클릭 핸들러의 안내문에 도달 불가) 사용자가 고장으로 인지했다.
            수집 항목 탭과 동일하게 사유를 상시 노출한다. */}
        {!validEventId ? (
          <p className="mt-2 text-xs" style={{ color: 'var(--content-muted)' }}>
            {t('cms.events.targets.saveFirstHint', {
              defaultValue: '먼저 이벤트를 저장한 뒤 대상 상품을 추가할 수 있습니다.',
            })}
          </p>
        ) : null}
        {error && (
          <p className="mt-2 text-xs" role="alert" style={{ color: 'var(--status-danger-fg)' }}>{error}</p>
        )}

        <div className="mt-3 flex justify-end">
          <Button size="sm" onClick={() => void handleAdd()} disabled={add.isPending || !validEventId}>
            <Plus size={14} className="mr-1" />
            {t('cms.events.targets.add')}
          </Button>
        </div>
      </div>

      {/* [event-form-fix] 스켈레톤은 실제 조회중일 때만 — 등록 모드(query disabled)는 v5 에서
          isPending=true 라 영원한 스켈레톤이 떠 "로딩 고장"처럼 보였다. 빈 목록으로 표시. */}
      {validEventId && query.isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(row) => row.targetId}
          emptyState={t('cms.events.targets.empty')}
        />
      )}
    </div>
  );
};
